import { useCallback, useRef, useState } from "react";
import { Upload, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

interface LinkedInDropzoneProps {
  multiple?: boolean;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
}

/**
 * Drag-and-drop minimalista pra PDFs. Usa HTML nativo + tailwind — sem
 * react-dropzone (mantém bundle leve).
 */
export function LinkedInDropzone({ multiple = true, disabled, onFiles }: LinkedInDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      const arr = Array.from(fileList).filter((f) => /\.pdf$/i.test(f.name));
      if (arr.length > 0) onFiles(arr);
    },
    [onFiles],
  );

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        if (disabled) return;
        handleFiles(e.dataTransfer.files);
      }}
      onClick={() => !disabled && inputRef.current?.click()}
      role="button"
      tabIndex={0}
      className={cn(
        "border-2 border-dashed rounded-lg p-12 flex flex-col items-center justify-center gap-3 text-center transition-colors cursor-pointer",
        "hover:border-primary/60 hover:bg-muted/40",
        isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/40",
        disabled && "opacity-50 cursor-not-allowed",
      )}
    >
      <div className="rounded-full bg-muted p-3">
        {isDragging ? <FileText className="h-6 w-6 text-primary" /> : <Upload className="h-6 w-6 text-muted-foreground" />}
      </div>
      <div>
        <p className="text-sm font-medium">
          {isDragging ? "Solte os PDFs aqui" : "Arraste e solte PDFs do LinkedIn"}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          ou clique para selecionar {multiple ? "um ou mais arquivos" : "um arquivo"}
        </p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          if (inputRef.current) inputRef.current.value = "";
        }}
      />
    </div>
  );
}
